<template>
  <div
    v-if="!isOwnContent"
    :class="['clap-simple', { 'is-unavailable': !canClap }]"
    role="button"
    tabindex="0"
    :aria-disabled="canClap ? null : 'true'"
    :aria-busy="isProcessing ? 'true' : null"
    :aria-label="accessibleLabel"
    :title="accessibleLabel"
    @click="handleClap($event)"
    @keydown.enter.prevent="handleClap($event)"
    @keydown.space.prevent="handleClap($event)"
  >
    <span v-if="showLabel" class="clap-label" aria-hidden="true">Clap</span>
    <svg
      class="clap-icon"
      :class="{ 'disabled-icon': !canClap }"
      aria-hidden="true"
    >
      <use href="@/assets/icons/iconset.svg#clap_smaller"></use>
    </svg>
    <span class="clap-count" v-if="clapCount > 0">{{ clapCount }}</span>

    <!-- Floating emojis -->
    <transition-group name="emoji-fade" tag="div" class="emoji-wrapper">
      <span
        v-for="emoji in clapEmojis"
        :key="emoji.id"
        class="clap-emoji"
        :style="emoji.style"
        aria-hidden="true"
      >
        {{ emoji.char }}
      </span>
    </transition-group>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from "vue";
import { useClapSession } from "@/composables/useClapSession";
import "vue3-toastify/dist/index.css";

export default {
  name: "ClapSimple",
  props: {
    // `{ itemId, orgId }` from resolveClapPlacement(item), or null when the
    // card is not an item placement. Claps pay that placement's owner.
    placement: { type: Object, default: null },
    // Legacy owner UID; only hides self-claps and is never sent anywhere.
    receiverUid: { type: String, default: null },
    showLabel: { type: Boolean, default: false },
  },
  setup(props) {
    const {
      clapCount,
      isProcessing,
      isOwnContent,
      availability,
      registerClap,
      notifyUnavailable,
    } = useClapSession({
      placement: () => props.placement,
      receiverUid: () => props.receiverUid,
    });
    const clapEmojis = ref([]);
    let emojiTimeout = null;

    const canClap = computed(() => availability.value.available);
    const accessibleLabel = computed(() =>
      canClap.value
        ? "Clap to show appreciation"
        : `Clap unavailable: ${availability.value.message}`,
    );

    const handleClap = async (event) => {
      if (!canClap.value) {
        notifyUnavailable();
        return;
      }
      const clickX = event?.clientX;
      const clickY = event?.clientY;
      const accepted = await registerClap();
      if (!accepted || !Number.isFinite(clickX) || !Number.isFinite(clickY)) {
        return;
      }

      for (let i = 0; i < accepted.increment; i++) {
        // Spawn emojis 25-100px to the left of click position
        const leftOffset = -(25 + Math.random() * 75);
        const initialOffsetY = (Math.random() - 0.5) * 20;
        clapEmojis.value.push({
          id: Date.now() + Math.random().toString(36).slice(2, 7) + i,
          char: accepted.emoji,
          style: {
            position: "fixed",
            top: `${clickY + initialOffsetY}px`,
            left: `${clickX + leftOffset}px`,
            opacity: 1,
            transform: "scale(1)",
            zIndex: 9999,
          },
        });
      }
      requestAnimationFrame(animateEmojisOutward);
      clearTimeout(emojiTimeout);
      emojiTimeout = setTimeout(() => {
        clapEmojis.value = [];
      }, 4200);
    };

    const animateEmojisOutward = () => {
      clapEmojis.value.forEach((emoji) => {
        const currentTop = parseFloat(emoji.style.top);
        const currentLeft = parseFloat(emoji.style.left);
        const drift = (Math.random() - 0.5) * 60;
        const rise = -(80 + Math.random() * 40);

        emoji.style.transition =
          "top 1.2s ease-out, left 1.2s ease-out, opacity 1.2s ease-out, transform 1.2s ease-out";
        emoji.style.top = `${currentTop + rise}px`;
        emoji.style.left = `${currentLeft + drift}px`;
        emoji.style.opacity = 0;
        emoji.style.transform =
          "scale(1.5) rotate(" + (Math.random() - 0.5) * 30 + "deg)";
      });
    };

    onBeforeUnmount(() => {
      clearTimeout(emojiTimeout);
    });

    return {
      accessibleLabel,
      canClap,
      clapCount,
      clapEmojis,
      handleClap,
      isOwnContent,
      isProcessing,
    };
  },
};
</script>

<style scoped>
.clap-simple {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  cursor: pointer;
  /* No padding, border, outline, or background */
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  outline: none;
  color: inherit;
  font: inherit;
}

.clap-simple:focus-visible {
  /* Minimal focus indicator */
  opacity: 0.8;
}

.clap-icon {
  width: 24px;
  height: 24px;
  fill: currentColor;
}

.clap-count {
  font-size: 14px;
  font-weight: 500;
  user-select: none;
}

.clap-label {
  white-space: nowrap;
  user-select: none;
}

.emoji-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  user-select: none;
  overflow: visible;
  z-index: 9999;
}

.clap-emoji {
  pointer-events: none;
  position: fixed;
  font-size: 32px;
  z-index: 9999;
  user-select: none;
  will-change: transform, opacity;
}

.emoji-fade-enter-active,
.emoji-fade-leave-active {
  transition: opacity 1.2s ease, transform 1.2s ease;
}

.emoji-fade-enter-from {
  opacity: 1;
  transform: scale(1);
}

.emoji-fade-leave-to {
  opacity: 0;
  transform: scale(1.5);
}

.clap-simple.is-unavailable {
  cursor: not-allowed;
}

.disabled-icon {
  opacity: 0.55;
  filter: grayscale(100%);
}
</style>
